import axiosInstance from '../../../lib/api/axiosInstance';

export interface DriveFile {
  id: string;
  name: string;
  mimeType: string;
  size: number;
  isFolder: boolean;
  createdTime: string;
  modifiedTime: string;
  webViewLink: string;
  webContentLink?: string;
  thumbnailLink?: string;
  parents: string[];
}

export interface DriveConnectionStatus {
  connected: boolean;
  connectedAt?: string;
  lastSyncAt?: string;
  tokenExpiry?: string;
}

export interface SyncItem {
  type: 'file' | 'folder';
  id: string;
  name?: string;
}

export interface SyncResult {
  success: boolean;
  synced: number;
  processed?: number;
  results: unknown[];
}

class GoogleDriveService {
  /**
   * Get OAuth authorization URL
   */
  async getAuthUrl(): Promise<{ authUrl: string }> {
    const response = await axiosInstance.get('/google-drive/auth-url');
    return response.data;
  }

  /**
   * Complete OAuth flow with authorization code
   */
  async connectDrive(code: string): Promise<{ success: boolean; connected_at: string }> {
    const response = await axiosInstance.post('/google-drive/connect', { code });
    return response.data;
  }

  /**
   * Disconnect Google Drive
   */
  async disconnectDrive(): Promise<{ success: boolean }> {
    const response = await axiosInstance.delete('/google-drive/disconnect');
    return response.data;
  }

  /**
   * Check Drive connection status
   */
  async getConnectionStatus(): Promise<DriveConnectionStatus> {
    const response = await axiosInstance.get('/google-drive/status');
    return response.data;
  }

  /**
   * Browse Drive files and folders
   */
  async browseDrive(folderId?: string, pageToken?: string): Promise<{
    files: DriveFile[];
    nextPageToken?: string;
  }> {
    const params = new URLSearchParams();
    if (folderId) params.append('folderId', folderId);
    if (pageToken) params.append('pageToken', pageToken);

    const response = await axiosInstance.get(`/google-drive/browse?${params.toString()}`);
    return response.data;
  }

  /**
   * Get file metadata
   */
  async getFileMetadata(fileId: string): Promise<DriveFile> {
    const response = await axiosInstance.get(`/google-drive/files/${fileId}/metadata`);
    return response.data;
  }

  /**
   * Sync items from Drive
   */
  async syncItems(
    hotelId: string,
    items: SyncItem[],
    processImmediately: boolean = false
  ): Promise<SyncResult> {
    const response = await axiosInstance.post('/google-drive/sync', {
      hotelId,
      items,
      processImmediately
    });
    return response.data;
  }

  /**
   * Sync files from Drive with options
   */
  async syncFiles(options: {
    fileIds: string[];
    hotelId: string;
    processImmediately?: boolean;
    folderOrganization?: 'flat' | 'by-type' | 'preserve';
  }): Promise<SyncResult> {
    const response = await axiosInstance.post('/google-drive/sync', {
      hotelId: options.hotelId,
      fileIds: options.fileIds,
      processImmediately: options.processImmediately || false,
      folderOrganization: options.folderOrganization || 'by-type',
    });
    return response.data;
  }

  /**
   * Get synced files for a hotel
   */
  async getSyncedFiles(hotelId: string): Promise<unknown[]> {
    const response = await axiosInstance.get(`/google-drive/synced/${hotelId}`);
    return response.data;
  }
}

const googleDriveService = new GoogleDriveService();
export default googleDriveService;
